import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpModule } from '@angular/http';
import { RouterModule, Routes, Router } from '@angular/router';

import { LayoutModule } from '@progress/kendo-angular-layout';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { GridModule, ExcelModule } from '@progress/kendo-angular-grid';

import { HotTableModule } from 'angular-handsontable';

import { PapaParseModule } from 'ngx-papaparse';

import { ToastrModule } from 'ngx-toastr';

import { ModalModule } from 'ngx-bootstrap/modal';
import { AccordionModule } from 'ngx-bootstrap/accordion';

import {Parser, parseString} from 'xml2js';

import { NgDragDropModule } from 'ng-drag-drop';

import {SlimLoadingBarModule} from 'ng2-slim-loading-bar';

import {Ng4LoadingSpinnerModule} from 'ng4-loading-spinner';

import { environment } from '../environments/environment';

import * as moment from 'moment';

import { AppComponent } from './app.component';
import { IntroComponent } from './intro/intro.component';
import { WorksheetComponent } from './worksheet/worksheet.component';

import { ConfigurationComponent } from './configuration/configuration.component';
import { MetadataComponent } from './metadata/metadata.component';
import { OrderOrLotsComponent } from './order-or-lots/order-or-lots.component';
import { ClinsComponent } from './clins/clins.component';
import { EndItemsComponent } from './end-items/end-items.component';
import { WbsComponent } from './wbs/wbs.component';
import { CostAccountsComponent } from './cost-accounts/cost-accounts.component';
import { FunctionalCategoriesComponent } from './functional-categories/functional-categories.component';
import { FunctionalCategoryRatePoolsComponent } from './functional-category-rate-pools/functional-category-rate-pools.component';
import { UnitsOrSublotsComponent } from './units-or-sublots/units-or-sublots.component';
import { CostHourDefinitionsComponent } from './cost-hour-definitions/cost-hour-definitions.component';
import { ReportingCalendarComponent } from './reporting-calendar/reporting-calendar.component';
import { SummaryCostDataComponent } from './summary-cost-data/summary-cost-data.component';
import { ActualCostHourDataComponent } from './actual-cost-hour-data/actual-cost-hour-data.component';
import { ForecastCostHourDataComponent } from './forecast-cost-hour-data/forecast-cost-hour-data.component';
import { ForecastAtCompletionCostHourComponent } from './forecast-at-completion-cost-hour/forecast-at-completion-cost-hour.component';
import { OrderOrLotAllocationMethodsComponent } from './order-or-lot-allocation-methods/order-or-lot-allocation-methods.component';
import { OrderOrLotAllocationComponentsComponent } from './order-or-lot-allocation-components/order-or-lot-allocation-components.component';
import { EndItemAllocationMethodsComponent } from './end-item-allocation-methods/end-item-allocation-methods.component';
import { EndItemAllocationComponentsComponent } from './end-item-allocation-components/end-item-allocation-components.component';
import { WbsAllocationMethodsComponent } from './wbs-allocation-methods/wbs-allocation-methods.component';
import { WbsAllocationComponentsComponent } from './wbs-allocation-components/wbs-allocation-components.component';
import { UnitOrSublotAllocationMethodsComponent } from './unit-or-sublot-allocation-methods/unit-or-sublot-allocation-methods.component';
import { UnitOrSublotAllocationComponentsComponent } from './unit-or-sublot-allocation-components/unit-or-sublot-allocation-components.component';
import { SummaryRemarksComponent } from './summary-remarks/summary-remarks.component';
import { WbsElementRemarksComponent } from './wbs-element-remarks/wbs-element-remarks.component';
import { WbsDictionaryDefinitionsComponent } from './wbs-dictionary-definitions/wbs-dictionary-definitions.component';
import { CostHourTagDefinitionsComponent } from './cost-hour-tag-definitions/cost-hour-tag-definitions.component';
import { MappingComponent } from './mapping/mapping.component';
import { ValidationComponent } from './validation/validation.component';
import { ReportDataService } from './report-data.service';
import { StartWizardModalComponent } from './start-wizard-modal/start-wizard-modal.component';
import { AccountsComponent } from './accounts/accounts.component';
import { FunctionalOverheadCategoriesComponent } from './functional-overhead-categories/functional-overhead-categories.component';
import { AllocationModalComponent } from './allocation-modal/allocation-modal.component';
import { MappedTablesComponent } from './mapped-tables/mapped-tables.component';
import { ProgressComponent } from './progress/progress.component';
import { ErrorModalComponent } from './error-modal/error-modal.component';
import { SvgComponent } from './svg/svg.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
library.add(fas, far);

const routes: Routes = [
  {path: environment.CONFIGURATION, component: ConfigurationComponent, outlet: 'editableTables'},
  {path: environment.METADATA, component: MetadataComponent, outlet: 'editableTables'},
  {path: environment.ORDER_OR_LOTS, component: OrderOrLotsComponent, outlet: 'editableTables'},
  {path: environment.CLINS, component: ClinsComponent, outlet: 'editableTables'},
  {path: environment.END_ITEMS, component: EndItemsComponent, outlet: 'generatedTables'},
  {path: environment.WBS, component: WbsComponent, outlet: 'generatedTables'},
  {path: environment.ACCOUNTS, component: AccountsComponent, outlet: 'generatedTables'},
  {path: environment.FUNCTIONAL_CATEGORIES, component: FunctionalCategoriesComponent, outlet: 'generatedTables'},
  {path: environment.FUNCTIONAL_OVERHEAD_CATEGORIES, component: FunctionalOverheadCategoriesComponent, outlet: 'generatedTables'},
  {path: environment.UNITS_OR_SUBLOTS, component: UnitsOrSublotsComponent, outlet: 'generatedTables'},
  {path: environment.COST_HOUR_DEFINITIONS, component: CostHourDefinitionsComponent, outlet: 'generatedTables'},
  {path: environment.REPORTING_CALENDAR, component: ReportingCalendarComponent, outlet: 'generatedTables'},
  {path: environment.SUMMARY_COST_DATA, component: SummaryCostDataComponent, outlet: 'editableTables'},
  {path: environment.ACTUAL_COST_HOUR_DATA, component: ActualCostHourDataComponent, outlet: 'generatedTables'},
  {path: environment.FORECAST_COST_HOUR_DATA, component: ForecastCostHourDataComponent, outlet: 'editableTables'},
  {path: environment.FORECAST_AT_COMPLETION_COST_HOUR, component: ForecastAtCompletionCostHourComponent, outlet: 'editableTables'},
  {path: environment.ORDER_OR_LOT_ALLOCATION_METHODS, component: OrderOrLotAllocationMethodsComponent, outlet: 'generatedTables'},
  {path: environment.ORDER_OR_LOT_ALLOCATION_COMPONENTS, component: OrderOrLotAllocationComponentsComponent, outlet: 'generatedTables'},
  {path: environment.END_ITEM_ALLOCATION_METHODS, component: EndItemAllocationMethodsComponent, outlet: 'generatedTables'},
  {path: environment.END_ITEM_ALLOCATION_COMPONENTS, component: EndItemAllocationComponentsComponent, outlet: 'generatedTables'},
  {path: environment.WBS_ALLOCATION_METHODS, component: WbsAllocationMethodsComponent, outlet: 'generatedTables'},
  {path: environment.WBS_ALLOCATION_COMPONENTS, component: WbsAllocationComponentsComponent, outlet: 'generatedTables'},
  {path: environment.UNIT_OR_SUBLOT_ALLOCATION_METHODS, component: UnitOrSublotAllocationMethodsComponent, outlet: 'generatedTables'},
  {path: environment.UNIT_OR_SUBLOT_ALLOCATION_COMPONENTS, component: UnitOrSublotAllocationComponentsComponent, outlet: 'generatedTables'},
  {path: environment.SUMMARY_REMARKS, component: SummaryRemarksComponent, outlet: 'editableTables'},
  {path: environment.WBS_ELEMENT_REMARKS, component: WbsElementRemarksComponent, outlet: 'editableTables'},
  {path: environment.WBS_DICTIONARY_DEFINITIONS, component: WbsDictionaryDefinitionsComponent, outlet: 'editableTables'},
  {path: environment.COST_HOUR_TAG_DEFINITIONS, component: CostHourTagDefinitionsComponent, outlet: 'editableTables'}
];
@NgModule({
  declarations: [
    AppComponent,
    IntroComponent,
    WorksheetComponent,
    ConfigurationComponent,
    MetadataComponent,
    OrderOrLotsComponent,
    ClinsComponent,
    EndItemsComponent,
    WbsComponent,
    CostAccountsComponent,
    FunctionalCategoriesComponent,
    FunctionalCategoryRatePoolsComponent,
    UnitsOrSublotsComponent,
    CostHourDefinitionsComponent,
    ReportingCalendarComponent,
    SummaryCostDataComponent,
    ActualCostHourDataComponent,
    ForecastCostHourDataComponent,
    ForecastAtCompletionCostHourComponent,
    OrderOrLotAllocationMethodsComponent,
    OrderOrLotAllocationComponentsComponent,
    EndItemAllocationMethodsComponent,
    EndItemAllocationComponentsComponent,
    WbsAllocationMethodsComponent,
    WbsAllocationComponentsComponent,
    UnitOrSublotAllocationMethodsComponent,
    UnitOrSublotAllocationComponentsComponent,
    SummaryRemarksComponent,
    WbsElementRemarksComponent,
    WbsDictionaryDefinitionsComponent,
    CostHourTagDefinitionsComponent,
    MappingComponent,
    ValidationComponent,
    StartWizardModalComponent,
    AccountsComponent,
    FunctionalOverheadCategoriesComponent,
    AllocationModalComponent,
    MappedTablesComponent,
    ProgressComponent,
    ErrorModalComponent,
    SvgComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ModalModule.forRoot(),
    AccordionModule.forRoot(),
    BrowserAnimationsModule,
    HttpModule,
    LayoutModule,
    ButtonsModule,
    HotTableModule,
    PapaParseModule,
    RouterModule.forRoot(routes),
    ToastrModule.forRoot(),
    Ng4LoadingSpinnerModule.forRoot(),
    GridModule,
    LayoutModule,
    ExcelModule,
    NgDragDropModule.forRoot(),
    SlimLoadingBarModule.forRoot(),
    FontAwesomeModule
  ],
  entryComponents: [StartWizardModalComponent, AllocationModalComponent, ErrorModalComponent],
  providers: [ReportDataService],
  bootstrap: [AppComponent],
})
export class AppModule { 
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts